import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeftIcon,
  CheckIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { SeasonService, ContestantService } from '../../../services/firebaseService';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { TRIBE_OPTIONS, getTribeColor } from '../../../utils/tribeUtils';

export default function TribeManagement() {
  const { seasonId } = useParams();
  const navigate = useNavigate();
  
  const [season, setSeason] = useState(null);
  const [contestants, setContestants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (seasonId) {
      loadData();
    }
  }, [seasonId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      // Load season data
      const seasonData = await SeasonService.getSeason(seasonId);
      if (!seasonData) {
        setError('Season not found');
        return;
      }
      setSeason(seasonData);

      // Load contestants
      const contestantsData = await ContestantService.getContestantsBySeason(seasonId);
      // Sort contestants by first name alphabetically
      const sortedContestants = contestantsData.sort((a, b) => {
        const firstNameA = a.name.split(' ')[0];
        const firstNameB = b.name.split(' ')[0];
        return firstNameA.localeCompare(firstNameB);
      });
      setContestants(sortedContestants);

    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleTribeChange = (contestantId, tribeIndex, tribeName) => {
    setContestants(prev => prev.map(contestant => {
      if (contestant.id === contestantId) {
        const newTribes = [...(contestant.tribes || [])];
        newTribes[tribeIndex] = tribeName;
        return { ...contestant, tribes: newTribes };
      }
      return contestant;
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      // Update each contestant with their tribe assignments
      const updatePromises = contestants.map(async (contestant) => {
        if (contestant.tribes && contestant.tribes.length > 0) {
          const contestantRef = doc(db, 'contestants', contestant.id);
          await updateDoc(contestantRef, {
            tribes: contestant.tribes
          });
        }
      });

      await Promise.all(updatePromises);
      setSuccess('Tribe assignments saved successfully!');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);

    } catch (err) {
      console.error('Error saving tribe assignments:', err);
      setError('Failed to save tribe assignments');
    } finally {
      setSaving(false);
    }
  };

  const renderTribeSwatch = (tribeName) => {
    if (!tribeName) return null;
    return (
      <div
        className="w-4 h-4 rounded border border-gray-300"
        style={{ backgroundColor: getTribeColor(tribeName) }}
        title={tribeName}
      />
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-survivor-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading tribe management...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate(`/admin/seasons/${seasonId}`)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-survivor-600 hover:bg-survivor-700"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Back to Season
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="px-4 py-6 sm:px-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={() => navigate(`/admin/seasons/${seasonId}`)}
                className="mr-4 p-2 text-gray-400 hover:text-gray-600"
              >
                <ArrowLeftIcon className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Tribe Management</h1>
                <p className="mt-1 text-sm text-gray-500">
                  {season.name} • Assign tribes to contestants
                </p>
              </div>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-survivor-600 hover:bg-survivor-700 disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <CheckIcon className="h-4 w-4 mr-2" />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div className="px-4 py-3 mb-4 bg-green-50 border border-green-200 rounded-md">
            <div className="flex">
              <CheckIcon className="h-5 w-5 text-green-400" />
              <p className="ml-2 text-sm text-green-800">{success}</p>
            </div>
          </div>
        )}

        {error && (
          <div className="px-4 py-3 mb-4 bg-red-50 border border-red-200 rounded-md">
            <div className="flex">
              <XMarkIcon className="h-5 w-5 text-red-400" />
              <p className="ml-2 text-sm text-red-800">{error}</p>
            </div>
          </div>
        )}

        {/* Contestants Grid */}
        <div className="px-4 py-6 sm:px-0">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {contestants.map(contestant => (
              <div key={contestant.id} className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-16 h-16 bg-gray-200 rounded-lg overflow-hidden relative flex-shrink-0">
                    {contestant.imageUrl ? (
                      <img
                        src={contestant.imageUrl}
                        alt={contestant.name}
                        className="absolute inset-x-0 top-0 w-full h-[300%] object-cover object-top rounded-lg"
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center text-gray-400 text-2xl">👤</div>
                    )}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-900">{contestant.name}</h3>
                    <p className="text-sm text-gray-500">Contestant</p>
                  </div>
                </div>

                {/* Tribe Assignments */}
                <div className="space-y-3">
                  {[1, 2, 3, 4].map(tribeIndex => (
                    <div key={tribeIndex} className="flex items-center space-x-3">
                      <label className="text-sm font-medium text-gray-700 w-8">
                        {tribeIndex}:
                      </label>
                      <select
                        value={contestant.tribes?.[tribeIndex - 1] || ''}
                        onChange={(e) => handleTribeChange(contestant.id, tribeIndex - 1, e.target.value)}
                        className="flex-1 text-sm border border-gray-300 rounded-md px-3 py-1 focus:outline-none focus:ring-2 focus:ring-survivor-500 focus:border-transparent"
                      >
                        <option value="">Select tribe...</option>
                        {TRIBE_OPTIONS.map(tribe => (
                          <option key={tribe} value={tribe}>{tribe}</option>
                        ))}
                      </select>
                      {renderTribeSwatch(contestant.tribes?.[tribeIndex - 1])}
                    </div>
                  ))}
                </div>

                {/* Current Tribe Swatches */}
                {contestant.tribes && contestant.tribes.length > 0 && (() => {
                  // Filter out empty strings and invalid tribe values
                  const validTribes = contestant.tribes.filter(tribe => tribe && tribe.trim() !== '' && tribe !== 'Select tribe...');
                  
                  if (validTribes.length === 0) return null;
                  
                  return (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <p className="text-xs text-gray-500 mb-2">Tribe History:</p>
                      <div className="flex space-x-1">
                        {validTribes.map((tribe, index) => (
                          <div
                            key={index}
                            className="flex-1 h-3 rounded-sm"
                            style={{ backgroundColor: getTribeColor(tribe) }}
                            title={`Tribe ${index + 1}: ${tribe}`}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
